/* import __COLOCATED_TEMPLATE__ from './setup-domain.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { captureException } from 'embercom/lib/sentry';
import type DomainService from 'embercom/services/domain-service';
import { type DomainEmailAddress, type Domain } from 'embercom/services/domain-service';
import type EntriConnectService from 'embercom/services/entri-connect-service';
import { type EntriConnectConfig } from 'embercom/services/entri-connect-service';
import type RouterService from '@ember/routing/router-service';
import type ValimailService from 'embercom/services/valimail-service';
import { VALIMAIL_DMARC_RUA } from 'embercom/services/valimail-service';

interface Signature {
  Args: {
    changeStep: (step: string | null, place: string) => void;
    domain: Domain | null;
    emailAddress: DomainEmailAddress | undefined;
    previousStepForDomainSetup: string | null;
  };
}

export default class SetupDomain extends Component<Signature> {
  @service declare intercomEventService: any;
  @service declare appService: AppService;
  @service declare entriConnectService: EntriConnectService;
  @service declare domainService: DomainService;
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare valimailService: ValimailService;

  @tracked verificationInProgress = false;
  @tracked showModal = false;
  @tracked selectedAuthMethod: 'automatic' | 'manual' = 'automatic';
  @tracked dmarcReportingEnabled = true;
  @tracked isInitializingValimail = false;

  place = 'setup-domain';

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.handleOnEntriClose = this.handleOnEntriClose.bind(this);
    window.addEventListener('onEntriClose', this.handleOnEntriClose);
    this.generateLink();
  }

  get dmarcAlreadySetup() {
    return this.args.domain?.dkimSettings?.validDmarcExists ?? false;
  }

  get valimailUserAlreadyCreated() {
    return this.appService.app.currentAdmin?.valimail_user_created ?? false;
  }

  get shouldInitializeValimail() {
    return !this.dmarcAlreadySetup && this.dmarcReportingEnabled && this.args.domain?.name;
  }

  get isLoading() {
    return this.isInitializingValimail || taskFor(this.entriConnectService.generateLink).isRunning;
  }

  async initializeValimailIfNeeded(): Promise<void> {
    if (!this.shouldInitializeValimail || this.isInitializingValimail) {
      return;
    }

    try {
      this.isInitializingValimail = true;
      await this.valimailService.initializeAccount(this.args.domain!.name);
    } catch (error) {
      console.error('Failed to initialize Valimail:', error);
      // Don't block the flow if Valimail fails
    } finally {
      this.isInitializingValimail = false;
    }
  }

  get dmarcRua(): string | null {
    // Only use RUA if DMARC is not already set up and toggle is ON
    if (!this.dmarcAlreadySetup && this.dmarcReportingEnabled) {
      return VALIMAIL_DMARC_RUA;
    }
    return null;
  }

  @action
  trackCopyButtonClick() {
    this.trackEvent({ action: 'clicked', object: 'copy_entri_share_link' });
  }

  @action
  selectAuthMethod(method: 'automatic' | 'manual') {
    this.selectedAuthMethod = method;
    this.trackEvent({
      action: 'selected',
      object:
        method === 'automatic' ? 'authenticate_automatically_tab' : 'authenticate_manually_tab',
    });
  }

  @action
  async toggleDmarcReporting(): Promise<void> {
    this.dmarcReportingEnabled = !this.dmarcReportingEnabled;

    // Clear Valimail state when toggle is turned off
    if (!this.dmarcReportingEnabled) {
      this.valimailService.reset();
    }

    // Regenerate link with updated RUA when toggle changes
    await this.generateLink();

    this.trackEvent({
      action: this.dmarcReportingEnabled ? 'turned_on' : 'turned_off',
      object: 'dmarc_reporting_toggle',
    });
  }

  @action
  async goToManualSetup() {
    await this.initializeValimailIfNeeded();
    this.args.changeStep('setup-domain-manually', this.place);
  }

  willDestroy() {
    super.willDestroy();
    window.removeEventListener('onEntriClose', this.handleOnEntriClose);
  }

  @action openModal() {
    if (!this.appService.app.onPricing5) {
      this.router.transitionTo('apps.app.settings.workspace.teammates');
      return;
    }

    if (this.permissionsService.currentAdminCan('can_manage_teammates')) {
      this.showModal = true;
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'invite_engineer',
      });
      return;
    }

    this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_manage_teammates');
    this.showModal = false;
  }

  get app() {
    return this.appService.app;
  }

  get entriLink(): string {
    return this.entriConnectService.link;
  }

  get config(): EntriConnectConfig {
    return {
      applicationId: 'intercom', // this is always the same
      token: this.entriConnectService.token,
      prefilledDomain: this.args.domain?.name ?? '',
      whiteLabel: this.entriConnectService.whitelabelOptions,
      dnsRecords: this.entriConnectService.dnsRecords,
      userId: this.entriConnectService.authenticationAttemptId,
    };
  }

  @action
  async showEntriModal(): Promise<void> {
    await this.initializeValimailIfNeeded();
    await this.generateLink();
    let config = this.config;
    window.entri?.showEntri(config);
    this.trackEvent({ action: 'clicked', object: 'connect_automatically' });
  }

  @action
  async generateLink(): Promise<void> {
    await taskFor(this.entriConnectService.generateLink).perform(
      Number(this.args.domain?.dkimSettings?.id),
      Number(this.args.domain?.bounceSettings?.id),
      this.dmarcRua,
    );
  }

  async handleOnEntriClose(event: any) {
    let success = event.detail.success;
    if (success) {
      this.handleEntriSuccess();
    }

    if (event.detail.error) {
      let entriError = event.detail.error;
      this.trackEvent({ action: 'failed', object: 'entri_modal' });
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.channels.email.connect-email.email-setup.setup-domain-step.connect-entri.error-notification',
        ),
      );
      captureException(new Error(entriError.title), {
        extra: {
          details: entriError.details,
          code: entriError.code,
          lastStatus: event.detail?.lastStatus,
          provider: event.detail?.provider,
          domain: event.detail?.domain,
          appId: this.appService.app.id,
        },
        tags: {
          responsibleTeam: 'team-channels',
        },
      });
    }
  }

  async handleEntriSuccess() {
    taskFor(this.entriConnectService.recordAppAdminEvent).perform(this.config.prefilledDomain);
    await this.updateAuthenticationAttemptsAndVerifyDomain();
    this.app.reload(); // reload the app in case the domain authentication caused a change on the reply email address
    this.trackEvent({ action: 'success', object: 'entri_modal' });
    if (this.args.emailAddress && this.args.previousStepForDomainSetup === 'overview') {
      this.args.changeStep('overview', this.place);
    } else {
      this.args.changeStep(null, this.place);
    }
  }

  async updateAuthenticationAttemptsAndVerifyDomain() {
    if (!this.args.domain) {
      return;
    }
    this.args.domain.dkimSettings.hasAuthenticationAttempt = true;
    this.args.domain.bounceSettings.hasAuthenticationAttempt = true;

    this.verificationInProgress = true;
    try {
      await this.args.domain.dkimSettings.verify();
    } catch (_error) {
      // do nothing and show next step
    }
    this.verificationInProgress = false;
  }

  trackEvent({ action, object }: { action: string; object: string }) {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      place: this.place,
      context: 'email-setup-sidebar',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Email::DomainsAndAddresses::SetupDomain': typeof SetupDomain;
    'new-settings/channels/email/domains-and-addresses/setup-domain': typeof SetupDomain;
  }
}
